import type { Dispatch, SetStateAction } from "react"

import type { UseMutationResult } from "react-query"
import { QueryClient } from "react-query"

import type { AxiosError, AxiosResponse } from "axios"
import type { FormikProps } from "formik"
import { v4 as uuidv4 } from "uuid"

import { Operators } from "../../types/custom/projects"
import type { CloneDataFilesRequest, Rule, UpdateTaglistRequest, VersionChangeRequest } from "../../types/custom/rules"
import type { ConditionListFile } from "../../types/generated/api/ConditionListFile"
import type { DataFileCloneResponse } from "../../types/generated/api/DataFileCloneResponse"
import { WorkflowSchemaFeatureRequest } from "../../types/generated/api/WorkflowSchemaFeatureRequest"
import type { GroupConditions, TagCardFormikValues, TagGroupConditions } from "./interface"

/**
 * Creates an empty condition object with default values
 * @returns {Rule} Empty condition object
 */
export const createEmptyCondition = (): Rule => ({
  id: uuidv4(),
  feature: "",
  andOr: "and",
  operator: Operators["="],
  type: "string",
  value: "",
  secondValue: "",
  valueOrFeature: "Value",
  schemaFeatureType: WorkflowSchemaFeatureRequest.type.TEXT,
})

/**
 * Creates a new group with default values
 * @returns {object} New group object
 */
export const createNewGroup = (): GroupConditions => ({
  id: uuidv4(),
  andOr: "and",
  conditions: [createEmptyCondition() as never, createEmptyCondition() as never],
  groups: [],
})

/**
 * Handles rule deletion logic
 */
export const handleDeleteRule = async (props: {
  name: string
  closeDialogState: Dispatch<SetStateAction<boolean>>
  values: TagCardFormikValues
  updateTaglistMutation: UseMutationResult<AxiosResponse, AxiosError, UpdateTaglistRequest>
  projectId: string
  taglistId: string
  queryClient: QueryClient
}): Promise<void> => {
  const { name, closeDialogState, values, updateTaglistMutation, projectId, taglistId, queryClient } = props
  const clonedArray = values.cards

  try {
    const finalFormattedRules = clonedArray?.map((rule) => {
      return { ...rule }
    })
    await updateTaglistMutation.mutateAsync({
      projectId: projectId,
      taglistId: taglistId,
      name: undefined,
      rules: [...(finalFormattedRules?.filter((rule) => rule.name !== name) ?? [])],
    })

    // Invalidate react-query queries in parallel
    await Promise.all([
      queryClient.invalidateQueries(["taglists", projectId]),
      queryClient.invalidateQueries("taglist"),
    ])

    closeDialogState(false)
  } catch (e) {
    console.warn(e)
  }
}

/**
 * Handles removing a rule from the form
 */
export const handleRemoveRule = (props: {
  isCreateMode: boolean
  isEditMode: boolean
  formik: FormikProps<TagCardFormikValues>
  ruleIndex: number
}): boolean => {
  const { isCreateMode, isEditMode, formik, ruleIndex } = props

  if (isCreateMode || isEditMode) {
    formik.setFieldValue(
      "cards",
      formik.values.cards.filter((_: TagGroupConditions, index: number) => index !== ruleIndex),
    )
    formik.setFieldError(`cards[${ruleIndex}]`, "")
    return false
  } else {
    return true
  }
}

/**
 * Handles saving imported taglist with file cloning
 */
export const saveImportedTaglistHandler = async (props: {
  uuid: string
  clonedFilesIdsMap: Record<string, string> | undefined
  setComponentsErrorMsgs:
    | ((fn: (prev: { [id: string]: string } | undefined) => { [id: string]: string } | undefined) => void)
    | undefined
  formik: FormikProps<TagCardFormikValues>
  t: (key: string) => string
  cloneDataFilesMutation: UseMutationResult<AxiosResponse<DataFileCloneResponse[]>, AxiosError, CloneDataFilesRequest>
  projectId: string
}): Promise<void> => {
  const { uuid, clonedFilesIdsMap, setComponentsErrorMsgs, formik, t, cloneDataFilesMutation, projectId } = props

  if (!clonedFilesIdsMap) return

  setComponentsErrorMsgs?.((prev) => {
    delete prev?.[uuid]
    return { ...prev }
  })

  if (formik.values.name.trim() === "") {
    setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: t("TaglistCard.validation.taglistNameRequired") }))
    return
  }

  const cardsFilesIds = new Set<string>()
  const clonedFilesIds = Object.values(clonedFilesIdsMap)

  for (const card of formik.values.cards) {
    if (card.condition_list_files.length > 0) {
      card.condition_list_files.forEach((file: ConditionListFile) => {
        if (file.uuid && !clonedFilesIds.includes(file.uuid))
          cardsFilesIds.add(JSON.stringify({ uuid: file.uuid, version: file.version }))
      })
    }
  }

  if (cardsFilesIds.size > 0) {
    await cloneDataFilesMutation.mutateAsync({
      project_uuid: projectId,
      data_files: Array.from(cardsFilesIds).map((file) => JSON.parse(file)),
    })
  }

  await formik.submitForm()
}

/**
 * Creates initial formik values for taglist
 */
export const createFormikInitialValues = (props: {
  uuid: string
  allComponentsDataRef?: { current: { [compId: string]: TagCardFormikValues } }
}): TagCardFormikValues => {
  const { uuid, allComponentsDataRef } = props

  return (
    allComponentsDataRef?.current[uuid] || {
      name: "",
      uuid: "",
      cards: [
        {
          id: `new-${uuidv4()}`,
          name: "",
          conditions: [createEmptyCondition()],
          groups: [],
          condition_list_files: [],
        } as TagGroupConditions,
      ],
    }
  )
}

/**
 * Handles changing taglist version
 */
export const changeTaglistVersion = async (props: {
  version: string
  taglistVersionChangeMutation: UseMutationResult<AxiosResponse, AxiosError, VersionChangeRequest>
  projectId: string
  uuid: string
  queryClient: QueryClient
}): Promise<void> => {
  const { version, taglistVersionChangeMutation, projectId, uuid, queryClient } = props

  try {
    await taglistVersionChangeMutation.mutateAsync({
      projectId: projectId,
      resourceId: uuid,
      resource: "taglists",
      version,
    })

    // Invalidate react-query queries
    await Promise.all([
      queryClient.invalidateQueries(["taglists", projectId]),
      queryClient.invalidateQueries(["taglist", uuid]),
      queryClient.invalidateQueries(["taglistVersionsList", uuid]),
    ])
  } catch {
    // Error handling is done in the mutation
  }
}

export const getGroupAndOrFieldPath = (props: {
  ruleIndex: number
  currentPath: number[]
  groupIndex: number
}): string => {
  const { ruleIndex, currentPath, groupIndex } = props

  if (currentPath.length <= 1) {
    return `cards[${ruleIndex}].groups[${groupIndex}].andOr`
  }

  return (
    currentPath
      .slice(0, -1)
      .reduce((acc, idx, i) => (i === 0 ? `cards[${ruleIndex}].groups[${idx}]` : `${acc}.groups[${idx}]`), "") +
    `.groups[${groupIndex}].andOr`
  )
}

export const getFieldArrayName = (props: { ruleIndex: number; currentPath: number[] }): string => {
  const { ruleIndex, currentPath } = props

  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].conditions`
  }

  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions`
}

export const getConditionId = (props: { ruleIndex: number; currentPath: number[]; conditionIndex: number }): string => {
  const { ruleIndex, currentPath, conditionIndex } = props

  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].conditions[${conditionIndex}]`
  }

  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions[${conditionIndex}]`
}

export const getConditionAndOrFieldPath = (props: {
  ruleIndex: number
  currentPath: number[]
  conditionIndex: number
}): string => {
  const { ruleIndex, currentPath, conditionIndex } = props

  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].conditions[${conditionIndex}].andOr`
  }

  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions[${conditionIndex}].andOr`
}

export const getAddConditionFieldPath = (props: { ruleIndex: number; currentPath: number[] }): string => {
  const { ruleIndex, currentPath } = props

  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].conditions`
  }

  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions`
}

export const getAddGroupFieldPath = (props: { ruleIndex: number; currentPath: number[] }): string => {
  const { ruleIndex, currentPath } = props

  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].groups`
  }

  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.groups`
}
